/* eslint-disable @typescript-eslint/no-unused-expressions */
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Server, Menu, X, Tv, CreditCard,
  Wallet, Package, BarChart2, Truck, ChevronDown, Layers, DollarSign,
  List, UserCog,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import logo from '@/assets/logo.png';
import { useAuth } from '@/contexts/AuthContext';

const standaloneTop = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
];

const groups = [
  {
    key: 'catalog',
    name: 'Catálogo',
    icon: Layers,
    items: [
      { name: 'Servicios', href: '/services', icon: Tv },
      { name: 'Combos', href: '/combos', icon: Package },
      { name: 'Cuentas', href: '/accounts', icon: Server },
    ],
  },
  {
    key: 'sales',
    name: 'Ventas',
    icon: DollarSign,
    items: [
      { name: 'Suscripciones', href: '/subscriptions', icon: CreditCard },
      { name: 'Pagos', href: '/payments', icon: Wallet },
    ],
  },
  {
    key: 'suppliers',
    name: 'Proveedores',
    icon: Truck,
    items: [
      { name: 'Proveedores', href: '/suppliers', icon: List },
      { name: 'Suscripciones', href: '/supplier-subscriptions', icon: CreditCard },
      { name: 'Pagos', href: '/supplier-payments', icon: Wallet },
    ],
  },
];

const standaloneBottom = [
  { name: 'Clientes', href: '/clients', icon: Users },
  { name: 'Colaboradores', href: '/collaborators', icon: UserCog },
  { name: 'Métricas', href: '/metrics', icon: BarChart2 },
];

function getInitialOpenGroups(pathname: string): Set<string> {
  const open = new Set<string>();
  groups.forEach((group) => {
    if (group.items.some((item) => pathname.startsWith(item.href))) {
      open.add(group.key);
    }
  });
  return open;
}

export function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const [openGroups, setOpenGroups] = useState<Set<string>>(() =>
    getInitialOpenGroups(location.pathname),
  );

  // Auto-expand group when navigating to a child route
  useEffect(() => {
    const active = getInitialOpenGroups(location.pathname);
    if (active.size > 0) {
      setOpenGroups((prev) => new Set([...prev, ...active]));
    }
  }, [location.pathname]);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(href);
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return 'U';
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-card border border-border shadow-sm"
      >
        <Menu className="h-5 w-5 text-foreground" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-sidebar border-r border-sidebar-border transition-transform duration-300 lg:translate-x-0 lg:static',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-sidebar-border shrink-0">
            <div className="flex items-center gap-2">
              <img src={logo} alt="VendePlay" className="w-8 h-8 object-contain" />
              <span className="font-semibold text-lg text-sidebar-foreground">VendePlayCRM</span>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden p-1 rounded hover:bg-sidebar-accent"
            >
              <X className="h-5 w-5 text-sidebar-foreground" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {/* Top standalone items */}
            {standaloneTop.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn('nav-item', isActive(item.href) && 'nav-item-active')}
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.name}</span>
              </NavLink>
            ))}

            {/* Divider */}
            <div className="pt-1 pb-1">
              <div className="h-px bg-sidebar-border" />
            </div>

            {/* Grouped items */}
            {groups.map((group) => {
              const isOpen = openGroups.has(group.key);
              const isGroupActive = group.items.some((item) => isActive(item.href));

              return (
                <div key={group.key}>
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className={cn(
                      'nav-item w-full justify-between',
                      isGroupActive && !isOpen && 'nav-item-active opacity-75',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <group.icon className="h-5 w-5" />
                      <span className="font-medium">{group.name}</span>
                    </div>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-muted-foreground transition-transform duration-200',
                        isOpen && 'rotate-180',
                      )}
                    />
                  </button>

                  {isOpen && (
                    <div className="mt-1 ml-3 pl-3 border-l border-sidebar-border space-y-0.5">
                      {group.items.map((item) => (
                        <NavLink
                          key={item.href}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className={cn('nav-item', isActive(item.href) && 'nav-item-active')}
                        >
                          <item.icon className="h-4 w-4" />
                          <span className="font-medium">{item.name}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Divider */}
            <div className="pt-1 pb-1">
              <div className="h-px bg-sidebar-border" />
            </div>

            {/* Bottom standalone items */}
            {standaloneBottom.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn('nav-item', isActive(item.href) && 'nav-item-active')}
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.name}</span>
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-sidebar-border shrink-0">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-medium text-sm">
                  {getInitials(user?.firstName, user?.lastName)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {user ? `${user.firstName} ${user.lastName}` : 'Usuario'}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  Cuenta: {user?.accountCode || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
